import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flame, ArrowRight, Check } from "lucide-react";
import { CHECKOUT_URL } from "@/lib/site";
import { CountdownTimer } from "@/components/landing/CountdownTimer";

const POINTS = ["Full access to every workout", "Lazy-friendly nutrition guide", "Cancel in one tap — pay $0 today"];

export const ExitPopup = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("exitPopupShown")) return;
    const onLeave = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) {
        setOpen(true);
        sessionStorage.setItem("exitPopupShown", "1");
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, []);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="exit-popup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
          onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-label="Last chance free trial offer">
          <motion.div initial={{ opacity: 0, scale: 0.92, y: 24 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative w-full max-w-md bg-white rounded-[2rem] p-8 sm:p-10 shadow-2xl text-center"
            onClick={(e) => e.stopPropagation()}>
            <button type="button" data-testid="exit-popup-close" onClick={() => setOpen(false)} aria-label="Close popup"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 hover:text-slate-800 transition-colors focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none">
              <X size={18} />
            </button>
            <span className="inline-flex w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 items-center justify-center mb-5"><Flame size={26} /></span>
            <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">Wait — your future self says don't go yet</h3>
            <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">Try the full app free for 7 days. If you don't feel the difference, cancel and pay nothing.</p>
            <p className="mt-6 text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400">This week's offer ends in</p>
            <div data-testid="exit-popup-countdown" className="mt-3"><CountdownTimer /></div>
            <ul className="mt-6 space-y-2.5 text-left max-w-xs mx-auto">
              {POINTS.map((p, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                  <Check size={17} className="text-blue-600 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <a data-testid="exit-popup-cta" href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 text-white px-8 py-4 text-lg font-bold transition-[transform,box-shadow] duration-300 shadow-[0_8px_20px_0_rgb(37,99,235,0.3)] hover:shadow-[0_12px_25px_0_rgb(37,99,235,0.4)] hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none focus-visible:ring-offset-2">
              Claim My 7-Day Free Trial <ArrowRight size={19} />
            </a>
            <button type="button" data-testid="exit-popup-dismiss" onClick={() => setOpen(false)}
              className="mt-4 text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none rounded">
              No thanks, I'll pass for now
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
